import BankAccountDetailsActions from '@/components/bankAccounts/BankAccountDetailsActions';
import BankAccountDetailsBasicInfo from '@/components/bankAccounts/BankAccountDetailsBasicInfo';
import BankAccountDetailsDepositSweeps from '@/components/bankAccounts/BankAccountDetailsDepositSweep';
import BankAccountDetailsFees from '@/components/bankAccounts/BankAccountDetailsFees';
import BankAccountDetailsTransactions from '@/components/bankAccounts/BankAccountDetailsTransactions';
import BankAccountDetailsView from '@/components/bankAccounts/BankAccountDetailsView';
import { SVButton, SVLoader } from '@/components/core';
import DefaultLayout from '@/components/layouts/DefaultLayout';
import { ROUTES } from '@/constants/routes';
import { useGetAuthorizedTeammatesList } from '@/hooks/requests/useAuthorizedTeammates';
import { useGetBankAccountDetails } from '@/hooks/requests/useBankAccounts';
import { useAuthUser } from '@/hooks/stores/useAuthUser';
import { generateBankAccountDisplayName } from '@/utils/bankAccounts';
import { Card, Grid, Stack, Typography } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';

export default function BankAccountsDetailsPage() {
  const navigate = useNavigate();
  const { isStaff } = useAuthUser();
  const { uuid = '' } = useParams();
  const { accountDetails, accountsDetailsIsPending } =
    useGetBankAccountDetails(uuid);
  const { authorizedTeammates, isAuthorizedTeammatesPending } =
    useGetAuthorizedTeammatesList({ bank_account_id: uuid, page: 0 });

  if (accountsDetailsIsPending || isAuthorizedTeammatesPending) {
    return (
      <DefaultLayout header="Bank Account Details">
        <SVLoader />
      </DefaultLayout>
    );
  }

  if (!accountDetails) {
    return (
      <DefaultLayout header="Bank Account Details">
        <Card>
          <Typography>
            Something went wrong retreiving the bank account. Our team has been
            notified.
          </Typography>
        </Card>
      </DefaultLayout>
    );
  }

  const {
    applicationType,
    business,
    primaryOwner,
    owners,
    aggregateInfo,
    depositSweep,
  } = accountDetails;
  const { fullName, email, id } = primaryOwner.profile;
  const { name: businessName } = business || {};
  const totalOwners = owners.length;

  const header = generateBankAccountDisplayName({
    applicationType,
    businessName,
    fullName,
    email,
    totalOwners,
  });

  return (
    <DefaultLayout
      header={header}
      buttons={[
        <SVButton
          onClick={() => navigate(`${ROUTES.BANK_ACCOUNTS}/${uuid}/edit`)}
          key="edit-account">
          Edit
        </SVButton>,
      ]}
      breadcrumb={[
        { label: 'Bank Accounts', href: ROUTES.BANK_ACCOUNTS },
        { label: header, isActive: true },
      ]}>
      <Stack spacing={2}>
        <BankAccountDetailsBasicInfo
          accountDetails={accountDetails}
          authorizedTeammates={authorizedTeammates}
        />
        <BankAccountDetailsFees accountDetails={accountDetails} />
        {isStaff && (
          <BankAccountDetailsDepositSweeps
            bankAccountId={uuid}
            depositSweep={depositSweep}
          />
        )}
        <BankAccountDetailsTransactions bankAccountId={uuid} />
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 6 }}>
            <BankAccountDetailsView aggregateInfo={aggregateInfo} />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <BankAccountDetailsActions
              accountDetails={accountDetails}
              customerId={id}
            />
          </Grid>
        </Grid>
      </Stack>
    </DefaultLayout>
  );
}
